import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWindowClose, FaSearch, FaTimes } from 'react-icons/fa';
import CommonModal from './CommonModal';
import {
  catchError,
  commonModalSetting,
  enterFn,
  outClick,
  maskingInfo,
} from 'JS/common';
import { searchUser, createSubscriptions } from 'JS/API';

const SubscriptionGrantModal = ({ setModal, setAlertBox, setAlert }) => {
  const [days, setDays] = useState('30');
  const [searchTarget, setSearchTarget] = useState('name');
  const [searchTxt, setSearchTxt] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [innerAlertBox, setInnerAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });
  const [innerAlert, setInnerAlert] = useState('');
  const navigate = useNavigate();

  // 회원 검색
  const handleSearch = async () => {
    if (!searchTxt.trim()) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '검색어를 입력해 주세요.'
      );
    }

    setSearching(true);
    const result = await searchUser(searchTarget, searchTxt, 1, 20);
    setSearching(false);

    if (typeof result === 'object') {
      const users = result?.data?.data || [];
      if (users.length === 0) {
        commonModalSetting(
          setInnerAlertBox,
          true,
          'alert',
          '검색 결과가 없습니다.'
        );
      }
      setSearchResults(users);
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
  };

  // 회원 선택
  const handleSelectUser = user => {
    const userPk = user.id || user.user_pk;
    const isAlreadySelected = selectedUsers.some(
      u => (u.id || u.user_pk) === userPk
    );
    if (isAlreadySelected) {
      commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '이미 선택된 회원입니다.'
      );
      return;
    }
    if (selectedUsers.length >= 100) {
      commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '최대 100명까지 선택 가능합니다.'
      );
      return;
    }
    setSelectedUsers([...selectedUsers, user]);
    setSearchResults([]);
    setSearchTxt('');
  };

  // 선택 회원 제거
  const handleRemoveUser = userPk => {
    setSelectedUsers(selectedUsers.filter(u => (u.id || u.user_pk) !== userPk));
  };

  const calculateExpectedEndDate = () => {
    const daysNum = parseInt(days, 10) || 0;
    if (daysNum < 1) return '-';
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + daysNum);
    return endDate.toISOString().substring(0, 10);
  };

  // 이용권 일괄 부여
  const handleGrant = async () => {
    if (selectedUsers.length === 0) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '이용권을 부여할 회원을 선택해 주세요.'
      );
    }

    const daysNum = parseInt(days, 10);
    if (!daysNum || daysNum < 1 || daysNum > 366) {
      return commonModalSetting(
        setInnerAlertBox,
        true,
        'alert',
        '부여 일수는 1일에서 366일 사이로<br/>입력해 주세요.'
      );
    }

    setLoading(true);
    const userPks = selectedUsers.map(u => u.id || u.user_pk);
    const result = await createSubscriptions(userPks, daysNum);
    setLoading(false);

    if (typeof result === 'object') {
      const createdCount = result?.data?.data?.created_count || selectedUsers.length;
      const message = result?.data?.message || `${createdCount}명의 회원에게 이용권이 발급되었습니다.`;

      setInnerAlert('success');
      commonModalSetting(setInnerAlertBox, true, 'alert', message);
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
  };

  useEffect(() => {
    window.addEventListener('click', e => outClick(e, setModal));
    return () => window.removeEventListener('click', e => outClick(e, setModal));
  }, []);

  return (
    <>
      <div className='modal-background'>
        <div className='modal subscription-grant'>
          <div className='topBar'>
            <h2>이용권 신규 발급</h2>
            <div onClick={() => setModal(false)}>
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div className='wrap'>
            {/* 회원 검색 */}
            <div className='search-section'>
              <label>회원 검색</label>
              <div className='search-row'>
                <select
                  value={searchTarget}
                  onChange={e => setSearchTarget(e.target.value)}>
                  <option value='name'>이름</option>
                  <option value='email'>이메일</option>
                  <option value='id'>아이디</option>
                </select>
                <input
                  type='text'
                  value={searchTxt}
                  onChange={e => setSearchTxt(e.target.value)}
                  onKeyDown={e => enterFn(e, handleSearch)}
                  placeholder='검색어 입력'
                />
                <button
                  className='search-btn'
                  onClick={handleSearch}
                  disabled={searching}>
                  <FaSearch />
                </button>
              </div>

              {/* 검색 결과 */}
              {searchResults.length > 0 && (
                <div className='search-results'>
                  {searchResults.map(user => {
                    const userPk = user.id || user.user_pk;
                    return (
                      <div
                        key={userPk}
                        className='search-result-item'
                        onClick={() => handleSelectUser(user)}>
                        <span className='name'>
                          {user.name ? maskingInfo('name', user.name) : '-'}
                        </span>
                        <span className='email'>
                          {user.email ? maskingInfo('email', user.email) : '-'}
                        </span>
                        <span className='id'>
                          {user.user_id ? maskingInfo('id', user.user_id) : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 선택된 회원 목록 */}
            <div className='selected-section'>
              <label>선택된 회원 ({selectedUsers.length}명)</label>
              <div className='selected-users'>
                {selectedUsers.length === 0 ? (
                  <div className='empty'>선택된 회원이 없습니다.</div>
                ) : (
                  selectedUsers.map(user => {
                    const userPk = user.id || user.user_pk;
                    return (
                      <div key={userPk} className='selected-user-item'>
                        <span className='name'>
                          {user.name ? maskingInfo('name', user.name) : '-'}
                        </span>
                        <span className='email'>
                          {user.email ? maskingInfo('email', user.email) : '-'}
                        </span>
                        <button
                          className='remove-btn'
                          onClick={() => handleRemoveUser(userPk)}>
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 부여 일수 */}
            <div className='input-section'>
              <label>부여 일수</label>
              <div className='input-row'>
                <input
                  type='number'
                  min='1'
                  max='366'
                  value={days}
                  onChange={e => setDays(e.target.value)}
                  placeholder='부여할 일수 입력'
                />
                <span className='suffix'>일</span>
              </div>
              <p className='hint'>1일 ~ 366일까지 입력 가능합니다.</p>
            </div>

            <div className='preview-section'>
              <div className='preview-row'>
                <span className='label'>예상 만료일</span>
                <span className='value highlight'>{calculateExpectedEndDate()}</span>
              </div>
            </div>

            <div className='footer'>
              <div className='btn-group'>
                <button
                  className='confirm-btn'
                  onClick={handleGrant}
                  disabled={loading || selectedUsers.length === 0}>
                  {loading ? '처리 중...' : '발급하기'}
                </button>
                <button className='cancel-btn' onClick={() => setModal(false)}>
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {innerAlertBox.bool && (
        <CommonModal
          setModal={setInnerAlertBox}
          modal={innerAlertBox}
          okFn={() => {
            if (innerAlert === 'success') {
              setModal(false);
            } else if (innerAlert === 'logout') {
              navigate('/');
            }
          }}
        />
      )}
    </>
  );
};

export default SubscriptionGrantModal;